"use client";

import { useState, useEffect } from "react";
import axiosInstance from "../../utils/axiosInstance";
import { API_ENDPOINTS } from "../../constants/config";
import { getFullName, getInitial } from "../../utils/urlUtils";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Mail, User, GraduationCap, Users, Heart } from "lucide-react";

function MyProfile() {
  const [student, setStudent] = useState(null);
  const [routeName, setRouteName] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchStudentProfile();
  }, []);

  useEffect(() => {
    if (!student) return;
    if (student.transportationType === "bus") {
      axiosInstance
        .get(API_ENDPOINTS.vehicleRouteStudent(student._id), {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
          _skipAuthRefresh: true,
        })
        .then((r) => {
          setRouteName(
            r.data?.data?.vehicleRoute?.name ||
            r.data?.data?.vehicleRoute?.vehicle?.vehicleNumber ||
            "—"
          );
        })
        .catch(() => setRouteName("—"));
    } else {
      setRouteName(null);
    }
  }, [student?._id, student?.transportationType]);

  const fetchStudentProfile = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const response = await axiosInstance.get(API_ENDPOINTS.usersProfile, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.status === "success" && response.data.data.user) {
        const userData = response.data.data.user;
        if (userData.enrollment && !userData.classMapping) {
          userData.classMapping = {
            class: userData.enrollment.class ?? null,
            section: userData.enrollment.section ?? null,
            academicYear: userData.enrollment.academicYear ?? null,
            rollNumber: userData.enrollment.rollNumber ?? null,
          };
        }
        setStudent(userData);
      } else {
        setError("Failed to fetch profile data");
      }
    } catch (err) {
      console.error("Error fetching student profile:", err);
      setError("Failed to fetch profile data: " + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const formatAddress = (address) => {
    if (!address) return "—";
    const { street, city, state, pincode } = address;
    return [street, city, state, pincode].filter(Boolean).join(", ") || "—";
  };

  const display = (v) => (v != null && v !== "" ? v : "—");

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[320px]">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-slate-300 border-t-slate-600" />
      </div>
    );
  }

  if (error && !student) {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 text-red-800 px-4 py-3 text-center">
        {error}
      </div>
    );
  }

  if (!student) return null;

  const className = student.classMapping?.class?.name ?? student.enrollment?.class?.name ?? "—";
  const sectionName = student.classMapping?.section?.name ?? student.enrollment?.section?.name ?? "—";
  const rollNumber = student.classMapping?.rollNumber ?? student.enrollment?.rollNumber ?? "—";
  const transportLabel = student.transportationType
    ? `${String(student.transportationType).charAt(0).toUpperCase()}${String(student.transportationType).slice(1)}${student.transportationType === "bus" && routeName ? ` (${routeName})` : ""}`
    : "—";

  const photo = student.profileImage || student.profilePicture;

  return (
    <div className="max-w-3xl mx-auto">
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6 pb-8 border-b border-slate-200">
        <Avatar className="h-24 w-24 rounded-2xl border-2 border-slate-200 shadow-sm">
          <AvatarImage src={photo} alt={getFullName(student)} />
          <AvatarFallback className="rounded-2xl bg-slate-100 text-slate-600 text-xl">
            {getInitial(student)}
          </AvatarFallback>
        </Avatar>
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold text-slate-800 truncate">
            {getFullName(student)}
          </h1>
          {student.username && (
            <p className="text-slate-500 text-sm mt-0.5">@{student.username}</p>
          )}
          <p className="text-slate-600 mt-1">
            {display(student.admissionNumber)} · Class {className} — Section {sectionName}
          </p>
        </div>
      </div>

      <div className="grid gap-8 mt-8">
        <Section title="Basic information" icon={User}>
          <dl className="grid gap-3 sm:grid-cols-2">
            <Row label="Admission number" value={display(student.admissionNumber)} />
            <Row label="Roll number" value={display(rollNumber)} />
            <Row label="Date of birth" value={student.birthday ? new Date(student.birthday).toLocaleDateString() : "—"} />
            <Row label="Gender" value={display(student.gender)} />
            <Row label="Blood group" value={display(student.bloodGroup)} />
            <Row label="Category" value={display(student.category)} />
            <Row label="Transportation" value={transportLabel} />
          </dl>
        </Section>

        <Section title="Contact" icon={Mail}>
          <dl className="grid gap-3 sm:grid-cols-2">
            <Row label="Email" value={display(student.email)} />
            <Row label="Phone" value={display(student.phoneNumber)} />
            <Row label="Address" value={formatAddress(student.address)} />
          </dl>
        </Section>

        <Section title="Academic" icon={GraduationCap}>
          <dl className="grid gap-3 sm:grid-cols-2">
            <Row label="Class" value={display(className)} />
            <Row label="Section" value={display(sectionName)} />
            <Row label="Roll number" value={display(rollNumber)} />
          </dl>
        </Section>

        <Section title="Guardian information" icon={Users}>
          <dl className="grid gap-3 sm:grid-cols-2">
            <Row label="Father's name" value={display(student.fatherName)} />
            <Row label="Father's contact" value={display(student.fatherContactNumber)} />
            <Row label="Mother's name" value={display(student.motherName)} />
            <Row label="Mother's contact" value={display(student.motherContactNumber)} />
            <Row label="Guardian's name" value={display(student.guardianName)} />
            <Row label="Guardian's contact" value={display(student.guardianContactNumber)} />
          </dl>
        </Section>

        <Section title="Medical & notes" icon={Heart}>
          <dl className="grid gap-3 sm:grid-cols-2">
            <Row label="Medical history" value={display(student.medicalHistory)} />
            <Row label="Special notes" value={display(student.note)} />
          </dl>
        </Section>
      </div>
    </div>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <section className="rounded-xl border border-slate-200 bg-slate-50/50 overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-200 bg-white">
        {Icon && <Icon className="h-4 w-4 text-slate-500" />}
        <h2 className="font-medium text-slate-800">{title}</h2>
      </div>
      <div className="p-4">{children}</div>
    </section>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-xs font-medium uppercase tracking-wider text-slate-500">{label}</dt>
      <dd className="text-slate-800">{value}</dd>
    </div>
  );
}

export default MyProfile;
